import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link, type LinkProps } from '@tanstack/react-router'
import { useState } from 'react'
import { type Invoice, type InvoiceSource, invoicesQueryOptions } from '@/entities/invoice'
import { companyProjectsQueryOptions } from '@/entities/project'
import {
  DeleteInvoiceDialog,
  EditInvoiceDialog,
  IssueInvoiceDialog,
} from '@/features/manage-invoice'
import { InvoiceActions } from '@/features/mark-invoice-paid'
import { type FileRef, flattenPages, zoneTitle } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { DateStamp } from '@/shared/ui/date-stamp'
import { EmptyState } from '@/shared/ui/empty-state'
import { useFileDownload } from '@/shared/ui/file-row'
import { Icon } from '@/shared/ui/icon'
import { Menu } from '@/shared/ui/menu'
import { MoneyAmount } from '@/shared/ui/money-amount'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'
import { StatusScreen } from '@/shared/ui/status-screen'
import { Table, TBody, TD, TH, THead, TR } from '@/shared/ui/table'
import { zoneColumnLabel } from '@/shared/ui/zone-badge'

interface InvoiceListProps {
  source: InvoiceSource
  isEmployee: boolean
  /** Ссылка на страницу счёта — роутинг остаётся у страницы. */
  invoiceLink: (invoiceId: string) => LinkProps
  /** Показывать колонку «Проект» (company-контур сотрудника). */
  showProjectColumn?: boolean
}

export function InvoiceList({
  source,
  isEmployee,
  invoiceLink,
  showProjectColumn,
}: InvoiceListProps) {
  const invoicesQ = useQuery(invoicesQueryOptions(source))
  // Резолв имени проекта для колонки «Проект» — только когда она показывается.
  const projectsQ = useInfiniteQuery({
    ...companyProjectsQueryOptions(source.kind === 'company' ? source.companyId : ''),
    enabled: source.kind === 'company' && Boolean(showProjectColumn),
  })
  const [issueOpen, setIssueOpen] = useState(false)

  if (invoicesQ.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (invoicesQ.error) {
    return <StatusScreen title="Не удалось загрузить счета" message="Обновите страницу." />
  }

  const invoices = invoicesQ.data
  const projectById = new Map(
    (projectsQ.data ? flattenPages(projectsQ.data.pages) : []).map((d) => [d.id, d]),
  )
  const hasActions = invoices.some((inv) => inv.status !== 'paid')

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <span className="label">Счета</span>
        {isEmployee && (
          <Button size="sm" variant="secondary" onClick={() => setIssueOpen(true)}>
            <Icon name="plus" size={16} />
            Счёт
          </Button>
        )}
      </div>

      {invoices.length === 0 ? (
        <EmptyState
          title="Счетов нет"
          description={isEmployee ? 'Выставите первый счёт.' : 'Здесь появятся ваши счета.'}
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Номер</TH>
              {showProjectColumn && <TH>Проект</TH>}
              <TH>Сумма</TH>
              <TH>Статус</TH>
              {/* «Выставлен» — момент: зона названа один раз в шапке колонки, а
                  не тегом в каждой строке. «Оплатить до» — календарный день,
                  зоны у него нет и приписывать её нельзя. */}
              <TH title={`${zoneTitle()}.`}>{zoneColumnLabel('Выставлен')}</TH>
              <TH>Оплатить до</TH>
              <TH>Файл</TH>
              {hasActions && (
                <TH>
                  <span className="sr-only">Действия</span>
                </TH>
              )}
            </TR>
          </THead>
          <TBody>
            {invoices.map((inv) => {
              const project = inv.project_id ? projectById.get(inv.project_id) : undefined
              return (
                <TR key={inv.id}>
                  <TD>
                    <Link
                      {...invoiceLink(inv.id)}
                      className="font-mono text-text-primary transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
                    >
                      {inv.number}
                    </Link>
                  </TD>
                  {showProjectColumn && (
                    <TD>
                      {inv.project_id == null ? (
                        <span className="text-sm text-text-tertiary">напрямую компании</span>
                      ) : project ? (
                        <Link
                          to="/$company/$project"
                          params={{ company: project.company_slug, project: project.slug }}
                          className="text-sm text-text-secondary transition-colors hover:text-accent hover:underline"
                        >
                          {project.title}
                        </Link>
                      ) : (
                        <span className="text-sm text-text-tertiary">—</span>
                      )}
                    </TD>
                  )}
                  <TD>
                    <MoneyAmount kopecks={inv.amount_kopecks} />
                  </TD>
                  <TD>
                    <StatusBadge status={inv.overdue ? 'overdue' : inv.status} />
                  </TD>
                  <TD className="whitespace-nowrap">
                    <DateStamp value={inv.issued_at} />
                  </TD>
                  <TD className="whitespace-nowrap">
                    {inv.due_date ? <DateStamp value={inv.due_date} deadline /> : '—'}
                  </TD>
                  <TD>
                    {/* Компактная иконка-скачивание: имя файла — на странице счёта
                        (в списке полный FileRow распирал бы таблицу). */}
                    <InvoiceFileButton file={inv.file} />
                  </TD>
                  {hasActions && (
                    <TD>
                      <div className="flex items-center justify-end gap-1">
                        <InvoiceActions
                          invoiceId={inv.id}
                          status={inv.status}
                          isEmployee={isEmployee}
                        />
                        {/* Правка и удаление — сотруднику и только пока счёт не
                            оплачен: оплаченный заморожен (бэкенд отвечает 409). */}
                        {isEmployee && inv.status !== 'paid' && <InvoiceRowMenu invoice={inv} />}
                      </div>
                    </TD>
                  )}
                </TR>
              )
            })}
          </TBody>
        </Table>
      )}

      {issueOpen && (
        <IssueInvoiceDialog
          target={
            source.kind === 'project'
              ? { kind: 'project', projectId: source.projectId }
              : { kind: 'company', companyId: source.companyId }
          }
          open
          onClose={() => setIssueOpen(false)}
        />
      )}
    </div>
  )
}

// Правка и удаление счёта строкой таблицы — за ⋯-меню, чтобы не распирать её
// кнопками рядом с переходами статуса.
function InvoiceRowMenu({ invoice }: { invoice: Invoice }) {
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  return (
    <>
      <Menu
        align="end"
        aria-label={`Действия со счётом ${invoice.number}`}
        trigger={<Icon name="dots" size={16} />}
        triggerClassName="p-1 text-text-tertiary"
        items={[
          { label: 'Изменить', icon: 'pencil', onSelect: () => setEditOpen(true) },
          { label: 'Удалить', icon: 'close', danger: true, onSelect: () => setDeleteOpen(true) },
        ]}
      />
      {editOpen && <EditInvoiceDialog invoice={invoice} open onClose={() => setEditOpen(false)} />}
      {deleteOpen && (
        <DeleteInvoiceDialog
          invoice={{ id: invoice.id, number: invoice.number }}
          open
          onClose={() => setDeleteOpen(false)}
        />
      )}
    </>
  )
}

// Компактная иконка-скачивание файла счёта (list-контекст): байты качаются
// fetch'ем и отдаются blob'ом с исходным именем. Стейт запроса — общий хук
// скачивания: на время запроса спиннер вместо иконки, клик заблокирован.
function InvoiceFileButton({ file }: { file: FileRef }) {
  const { busy, download } = useFileDownload()
  if (!file.url) return <span className="text-text-tertiary">—</span>
  return (
    <button
      type="button"
      disabled={busy}
      aria-label={`Скачать ${file.file_name}`}
      title={file.file_name}
      onClick={() => void download(file)}
      className="inline-flex text-text-tertiary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent disabled:opacity-40"
    >
      {busy ? (
        <Spinner label={`Скачиваем ${file.file_name}`} />
      ) : (
        <Icon name="download" size={18} />
      )}
    </button>
  )
}
